import type { DashboardSnapshot } from "../../../../lib/dashboard";
import {
  formatCompactCurrency,
  mapFinanceReadinessTier,
} from "../../../../lib/dashboard";
import { WORK_HISTORY_OPTIONS } from "../../../../lib/onboarding-types";
import type { FinancialInputMeta } from "../../../../lib/onboarding-types";
import type { ProfileRowTier } from "./tier-dot";
import type { AggregateInputRowProps } from "./aggregate-input-row";

// Returns the STRONGEST verification tier across the given field names.
//
// Why strongest, not weakest: this row badge tells the user "what's the
// best thing we can say about this row" — which mirrors what they'd
// share with a lender or agent. If a household has 1 self-reported
// manual savings entry alongside 15 bank-verified Plaid accounts, the
// row should read Verified, not Self-reported. Otherwise a single
// stale manual line drags the badge down to the worst rung.
//
// Mirrors the backend's `strongestVerificationTier` in
// `internal/services/financials/facts/service.go` (line ~1180) so the
// row-level summary stays consistent with the aggregate verification
// tier the backend writes into `financial_inputs[*].verification_tier`.
//
// Today every aggregate row is single-field, so min vs max is identical
// — this function just guarantees the right answer if a future row
// aggregates multiple fields (e.g. employment may someday combine
// `household_income` and a dedicated employment field).
function pickTier(
  snapshot: DashboardSnapshot,
  fields: string[],
): ProfileRowTier {
  const inputs: FinancialInputMeta[] = snapshot.financialInputs ?? [];

  const tiers: ProfileRowTier[] = [];
  for (const fieldName of fields) {
    const input = inputs.find((i) => i.field === fieldName);
    if (input && input.present) {
      tiers.push(mapFinanceReadinessTier(input.verification_tier));
    }
  }

  if (tiers.length === 0) return "self-reported";

  const order: ProfileRowTier[] = [
    "self-reported",
    "document-backed",
    "bank-verified",
    "lender-approved",
  ];
  const maxIdx = Math.max(...tiers.map((t) => order.indexOf(t)));
  return order[maxIdx] ?? "self-reported";
}

function parseNumberDraft(value: string) {
  return Number(value);
}

export const workHistoryOptions = WORK_HISTORY_OPTIONS.map((option) => ({
  label: option.label,
  value: String(option.value),
}));

export function formatWorkHistory(value: number | null | undefined) {
  if (value == null) return "Not set";
  return (
    WORK_HISTORY_OPTIONS.find((option) => option.value === value)?.label ??
    `${value} mo`
  );
}

// buildAggregateRows derives AggregateInputRowProps for the 5 aggregate rows
// (Income, Employment, Cash & Assets, Debts, Personal & ID) from a snapshot.
// Row order matches decision §3: Income → Employment → Cash & Assets → Debts → Personal & ID.
// Credit is NOT included here — it is rendered separately as a CreditRow in InputsColumn.
//
// SILL-302: the three financial rows (income / cash_assets / debts) are
// list-only — they don't surface a scalar editor. All edits flow through
// the manual-entries API via `manualEntries` on the row. The summary line
// reflects the snapshot's projected aggregate, which the facts pipeline
// builds from manual_entries (and any doc/Plaid sources). Employment and
// Personal & ID rows are unaffected.
export function buildAggregateRows(
  snapshot: DashboardSnapshot,
): AggregateInputRowProps[] {
  return [
    {
      rowId: "income",
      label: "Income",
      summary:
        snapshot.householdIncome > 0
          ? `${formatCompactCurrency(snapshot.householdIncome)}/yr`
          : "Add income",
      tier: pickTier(snapshot, ["household_income"]),
      sharpenHint: "Connect payroll to verify income.",
    },
    {
      edit: {
        field: "employment_months",
        formatValue: (draft) =>
          draft === "" ? "Not set" : formatWorkHistory(Number(draft)),
        inputType: "select",
        label: "Work history",
        options: workHistoryOptions,
        parseValue: parseNumberDraft,
        placeholder: "Select work history",
        value: snapshot.employmentMonths ?? null,
      },
      rowId: "employment",
      label: "Employment",
      summary: formatWorkHistory(snapshot.employmentMonths),
      tier: pickTier(snapshot, ["employment_months"]),
      sharpenHint: "Upload a recent paystub or VOE letter.",
    },
    {
      rowId: "cash_assets",
      label: "Cash & Assets",
      summary:
        snapshot.savings > 0
          ? formatCompactCurrency(snapshot.savings)
          : "Add account",
      tier: pickTier(snapshot, ["savings"]),
      sharpenHint: "Connect your bank to verify reserves.",
    },
    {
      rowId: "debts",
      label: "Existing debts",
      summary:
        snapshot.monthlyDebts > 0
          ? `${formatCompactCurrency(snapshot.monthlyDebts)}/mo`
          : "None reported",
      tier: pickTier(snapshot, ["monthly_debts"]),
      sharpenHint: "Connect accounts to verify monthly obligations.",
    },
    {
      rowId: "personal_identity",
      label: "Personal & ID",
      summary: "Not started",
      tier: "self-reported",
      sharpenHint: "Verify your identity to unlock the marketplace.",
    },
  ];
}
